import React from "react";
//#region types
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Box, Grid } from "@mui/material";
import { ActionLinkButton, Icon, InfoTooltip } from "components/common";
//#endregion
import { IconDefinitionConstants } from "utils/constants";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

export interface ReferenceCodeCaptionProps {
  code?: string;
  hasPerson: boolean;
  onLinkClick: () => void;
}

/**
 * Caption + link row shown under the reference person field, telling the user whether the
 * selected person has a reference code and letting them add/change it inline. Shared between
 * the create-invoice customer step and the preview "Skicka som" box, per the design spec's
 * requirement that both use the exact same treatment.
 */
const ReferenceCodeCaption: React.FC<ReferenceCodeCaptionProps> = ({ code, hasPerson, onLinkClick }) => {
  const hasCode = !!code;

  return (
    <Grid container alignItems="flex-start" wrap="nowrap" columnGap={1}>
      <Grid item className={cssExports.tooltipIconAlign} sx={{ flexShrink: 0 }}>
        <InfoTooltip
          text={LocalizationHelperInstance.getFormattedMessage(
            hasPerson
              ? TranslationKeys.Invoice.ReferenceCodeTooltipWithPerson
              : TranslationKeys.Invoice.ReferenceCodeTooltipNoPerson
          )}
        />
      </Grid>
      {/* The row can't wrap and its container clips overflow, so an over-long code would otherwise
          push the tooltip and the add/change link out of view — let the code itself ellipsize instead. */}
      <Box
        sx={{ display: "flex", flexWrap: "nowrap", columnGap: "4px", minWidth: 0 }}
        className={cssExports.referenceCodeCaptionText}
      >
        {hasCode ? (
          <>
            <span>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReferenceCodeLabel)}</span>
            <Box
              component="span"
              className={cssExports.referenceCodeCaptionValue}
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {code}
            </Box>
          </>
        ) : (
          <span>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReferenceCodeMissingLabel)}
          </span>
        )}
      </Box>
      <Grid item sx={{ display: "flex", flexShrink: 0 }}>
        <ActionLinkButton
          endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
          className={clsx(cssExports.seeMoreLink, cssExports.animatedLink)}
          onClick={onLinkClick}
        >
          {hasCode
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReferenceCodeChangeLink)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Add)}
        </ActionLinkButton>
      </Grid>
    </Grid>
  );
};

export default ReferenceCodeCaption;
